/**
 * BODS / TransXChange Exporter (Fixed Routes → UK BODS)
 *
 * Produces a TransXChange (PTI-profile-oriented, schema 2.4) document an operator
 * can publish to the UK Bus Open Data Service. We reuse `buildGtfsFeed` to turn
 * Fixed Routes into the GTFS intermediate (trip synthesis, calendar dedup, id
 * resolution, after-midnight times — all already solved there), then MAP that GTFS
 * feed onto TransXChange elements (`transXChangeFromGtfsFeed`, the testable core):
 *
 *   agency (+NOC)         -> Operators/Operator
 *   route                 -> Services/Service + Lines/Line
 *   distinct stop pattern -> JourneyPattern + JourneyPatternSection + RouteSection
 *   trip                  -> VehicleJourney (DepartureTime)
 *   stop_times runtimes   -> JourneyPatternTimingLink RunTime / WaitTime
 *   calendar              -> OperatingProfile + OperatingPeriod
 *   stop (lat/lng)        -> AnnotatedStopPointRef (NaPTAN AtcoCode when known)
 *
 * NaPTAN: BODS references stops by ATCO code. BODS-origin stops carry their ATCO
 * code (as `FixedRouteStop.atcoCode` and/or as the GTFS stop_id); we use it. Stops
 * drawn from scratch have no ATCO — we emit a synthetic ref + `Location` lat/lng
 * and count them so the UI can warn ("not NaPTAN-matched"). Publishing is manual:
 * this produces a compliant file; the operator uploads it to their BODS account.
 *
 * Runs entirely in the browser (string XML + jszip).
 * See FeaturePlans/BodsIntegration/prd-00-master-index.md (PRD-02/PRD-04).
 */

import JSZip from 'jszip';

import type { FixedRoutesState, FixedRouteStop } from '../../types/fixedRoutes';
import type { GTFSCalendarRow } from './gtfsConverter';
import {
  buildGtfsFeed,
  type GtfsExportFeed,
  type GtfsExportOptions,
  type SkippedRoute,
} from './gtfsExporter';

/** Operator identity required by BODS/TransXChange (collected in the export modal). */
export interface BodsOperatorMeta {
  /** National Operator Code (NOC), e.g. "OP01". Required by BODS. */
  nationalOperatorCode: string;
  /** Operator legal/trading name. */
  operatorName: string;
  /** PSV operator licence number (e.g. "PB0000000"); optional in this draft. */
  licenceNumber?: string;
}

export interface BodsExportOptions extends GtfsExportOptions {
  operator: BodsOperatorMeta;
  /** ISO-ish creation stamp for the TransXChange header (pass in; date APIs vary). */
  creationDateTime: string;
}

export interface BodsExportResult {
  xml: string;
  warnings: string[];
  skippedRoutes: SkippedRoute[];
  counts: {
    services: number;
    lines: number;
    journeyPatterns: number;
    vehicleJourneys: number;
    stopPoints: number;
    /** Stops emitted without a NaPTAN ATCO code (synthetic ref) — surface as a warning. */
    stopsWithoutAtco: number;
  };
}

/** Parameters for the GTFS-feed → TransXChange core (decoupled from FixedRoutes). */
export interface TransXChangeParams {
  operator: BodsOperatorMeta;
  feedName: string;
  creationDateTime: string;
  defaultServiceDateRange: { startDate: string; endDate: string };
  /** GTFS stop_id → NaPTAN ATCO code, for stops that carry one. */
  atcoByGtfsId?: Map<string, string>;
  seedWarnings?: string[];
  skippedRoutes?: SkippedRoute[];
}

// --- small helpers ---------------------------------------------------------

const esc = (v: unknown): string =>
  String(v ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

/** GTFS "HH:MM:SS" (may exceed 24h) → seconds since midnight. */
const toSeconds = (t?: string): number => {
  if (!t) return 0;
  const [h, m, s] = t.split(':').map((n) => Number(n) || 0);
  return h * 3600 + m * 60 + s;
};

/** Seconds since midnight → xsd:time "HH:MM:SS", normalised into a 24h clock
 * (after-midnight trips wrap; the service day is conveyed by OperatingProfile). */
const secondsToClock = (secs: number): string => {
  const v = ((secs % 86400) + 86400) % 86400;
  const h = Math.floor(v / 3600);
  const m = Math.floor((v % 3600) / 60);
  const s = v % 60;
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(h)}:${pad(m)}:${pad(s)}`;
};

/** Non-negative duration in seconds → ISO-8601 "PT#M#S" (TransXChange RunTime/WaitTime). */
const isoDuration = (secs: number): string => {
  const v = Math.max(0, Math.round(secs));
  const m = Math.floor(v / 60);
  const s = v % 60;
  if (m && s) return `PT${m}M${s}S`;
  if (m) return `PT${m}M`;
  return `PT${s}S`;
};

/** GTFS date "YYYYMMDD" (or "YYYY-MM-DD") → TransXChange "YYYY-MM-DD". */
const toTxcDate = (d?: string): string | undefined => {
  if (!d) return undefined;
  if (/^\d{8}$/.test(d)) return `${d.slice(0, 4)}-${d.slice(4, 6)}-${d.slice(6, 8)}`;
  return d;
};

/** Heuristic: does this id look like a NaPTAN ATCO code (3-digit area + alphanum)? */
const looksLikeAtco = (id: string): boolean => /^[0-9]{3}[0-9A-Za-z]{5,9}$/.test(id);

const DOW: Array<keyof GTFSCalendarRow> = [
  'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday',
];
const DOW_EL: Record<string, string> = {
  monday: 'Monday', tuesday: 'Tuesday', wednesday: 'Wednesday', thursday: 'Thursday',
  friday: 'Friday', saturday: 'Saturday', sunday: 'Sunday',
};

const operatingProfileXml = (cal: GTFSCalendarRow | undefined, indent: string): string => {
  const days = cal ? DOW.filter((d) => cal[d] === '1') : [];
  const inner = days.length
    ? days.map((d) => `<${DOW_EL[d as string]}/>`).join('')
    : '<HolidaysOnly/>';
  return [
    `${indent}<OperatingProfile>`,
    `${indent}  <RegularDayType>`,
    `${indent}    <DaysOfWeek>${inner}</DaysOfWeek>`,
    `${indent}  </RegularDayType>`,
    `${indent}</OperatingProfile>`,
  ].join('\n');
};

/** Recover the GTFS stop_id `buildGtfsFeed` would assign to a FixedRouteStop. */
const gtfsStopIdBase = (s: FixedRouteStop): string =>
  s.uatStopId?.trim()
  || (s.id.startsWith('gtfs-stop-') ? s.id.slice('gtfs-stop-'.length) : s.id);

// --- core: GTFS feed → TransXChange ----------------------------------------

export function transXChangeFromGtfsFeed(
  feed: GtfsExportFeed,
  params: TransXChangeParams,
): BodsExportResult {
  const warnings = [...(params.seedWarnings ?? [])];
  const atcoByGtfsId = params.atcoByGtfsId ?? new Map<string, string>();

  const calBySvc = new Map<string, GTFSCalendarRow>();
  for (const c of feed.calendar) calBySvc.set(c.service_id, c);

  const stopById = new Map(feed.stops.map((s) => [s.stop_id, s]));

  const timesByTrip = new Map<string, typeof feed.stopTimes>();
  for (const st of feed.stopTimes) {
    const arr = timesByTrip.get(st.trip_id) ?? [];
    arr.push(st);
    timesByTrip.set(st.trip_id, arr);
  }
  for (const arr of Array.from(timesByTrip.values())) {
    arr.sort((a, b) => Number(a.stop_sequence) - Number(b.stop_sequence));
  }

  const tripsByRoute = new Map<string, typeof feed.trips>();
  for (const tr of feed.trips) {
    const arr = tripsByRoute.get(tr.route_id) ?? [];
    arr.push(tr);
    tripsByRoute.set(tr.route_id, arr);
  }

  const referencedStopIds = new Set<string>();
  const servicesXml: string[] = [];
  const routesXml: string[] = [];
  const routeSectionsXml: string[] = [];
  const jpSectionsXml: string[] = [];
  const vehicleJourneysXml: string[] = [];

  const opId = 'OP1';
  let lineCounter = 0;
  let jpCounter = 0;
  let vjCounter = 0;
  let patternCounter = 0;

  const noc = params.operator.nationalOperatorCode.trim() || 'UNKNOWN';

  for (const route of feed.routes) {
    const trips = (tripsByRoute.get(route.route_id) ?? []).filter(
      (t) => (timesByTrip.get(t.trip_id) ?? []).length >= 2,
    );
    if (trips.length === 0) continue;

    lineCounter += 1;
    const lineId = `L${lineCounter}`;
    const serviceCode = `${noc}:${(route.route_short_name || route.route_id).replace(/[^A-Za-z0-9_-]/g, '') || route.route_id}`;
    const lineName = route.route_short_name || route.route_long_name || route.route_id;

    // Group this route's trips by stop-sequence signature → one JourneyPattern each.
    const groups = new Map<string, typeof trips>();
    for (const t of trips) {
      const sig = (timesByTrip.get(t.trip_id) ?? []).map((st) => st.stop_id).join('>');
      const arr = groups.get(sig) ?? [];
      arr.push(t);
      groups.set(sig, arr);
    }

    const journeyPatternsXml: string[] = [];
    const jpRefByTrip = new Map<string, string>();
    let origin = lineName;
    let destination = lineName;

    for (const groupTrips of Array.from(groups.values())) {
      const rep = groupTrips[0];
      const repTimes = timesByTrip.get(rep.trip_id) ?? [];
      repTimes.forEach((st) => referencedStopIds.add(st.stop_id));

      patternCounter += 1;
      jpCounter += 1;
      const jpId = `JP${jpCounter}`;
      const jpsId = `JPS${patternCounter}`;
      const rsId = `RS${patternCounter}`;
      const firstStop = stopById.get(repTimes[0].stop_id);
      const lastStop = stopById.get(repTimes[repTimes.length - 1].stop_id);
      origin = firstStop?.stop_name || origin;
      destination = lastStop?.stop_name || destination;

      const timingLinks: string[] = [];
      const routeLinks: string[] = [];
      for (let i = 0; i < repTimes.length - 1; i += 1) {
        const from = repTimes[i];
        const to = repTimes[i + 1];
        const runSecs = toSeconds(to.arrival_time) - toSeconds(from.departure_time || from.arrival_time);
        const waitSecs = toSeconds(from.departure_time) - toSeconds(from.arrival_time);
        const tlId = `${jpsId}-TL${i + 1}`;
        const rlId = `${rsId}-RL${i + 1}`;
        const waitXml = waitSecs > 0 ? `\n          <WaitTime>${isoDuration(waitSecs)}</WaitTime>` : '';
        timingLinks.push(
          [
            `      <JourneyPatternTimingLink id="${tlId}">`,
            `        <From>`,
            `          <StopPointRef>${esc(from.stop_id)}</StopPointRef>${waitXml}`,
            `        </From>`,
            `        <To>`,
            `          <StopPointRef>${esc(to.stop_id)}</StopPointRef>`,
            `        </To>`,
            `        <RouteLinkRef>${rlId}</RouteLinkRef>`,
            `        <RunTime>${isoDuration(runSecs)}</RunTime>`,
            `      </JourneyPatternTimingLink>`,
          ].join('\n'),
        );
        routeLinks.push(
          [
            `      <RouteLink id="${rlId}">`,
            `        <From><StopPointRef>${esc(from.stop_id)}</StopPointRef></From>`,
            `        <To><StopPointRef>${esc(to.stop_id)}</StopPointRef></To>`,
            `      </RouteLink>`,
          ].join('\n'),
        );
      }
      jpSectionsXml.push(
        `    <JourneyPatternSection id="${jpsId}">\n${timingLinks.join('\n')}\n    </JourneyPatternSection>`,
      );
      routeSectionsXml.push(
        `    <RouteSection id="${rsId}">\n${routeLinks.join('\n')}\n    </RouteSection>`,
      );
      routesXml.push(
        [
          `    <Route id="R${patternCounter}">`,
          `      <PrivateCode>${esc(serviceCode)}-${patternCounter}</PrivateCode>`,
          `      <Description>${esc(lineName)}</Description>`,
          `      <RouteSectionRef>${rsId}</RouteSectionRef>`,
          `    </Route>`,
        ].join('\n'),
      );

      const direction = rep.direction_id === '1' ? 'inbound' : 'outbound';
      journeyPatternsXml.push(
        [
          `        <JourneyPattern id="${jpId}">`,
          `          <DestinationDisplay>${esc(rep.trip_headsign || destination)}</DestinationDisplay>`,
          `          <Direction>${direction}</Direction>`,
          `          <RouteRef>R${patternCounter}</RouteRef>`,
          `          <JourneyPatternSectionRefs>${jpsId}</JourneyPatternSectionRefs>`,
          `        </JourneyPattern>`,
        ].join('\n'),
      );
      for (const t of groupTrips) jpRefByTrip.set(t.trip_id, jpId);
    }

    for (const t of trips) {
      const times = timesByTrip.get(t.trip_id) ?? [];
      const dep = times[0]?.departure_time || times[0]?.arrival_time;
      vjCounter += 1;
      vehicleJourneysXml.push(
        [
          `  <VehicleJourney>`,
          `    <VehicleJourneyCode>VJ${vjCounter}</VehicleJourneyCode>`,
          `    <ServiceRef>${esc(serviceCode)}</ServiceRef>`,
          `    <LineRef>${lineId}</LineRef>`,
          `    <JourneyPatternRef>${jpRefByTrip.get(t.trip_id)}</JourneyPatternRef>`,
          `    <DepartureTime>${secondsToClock(toSeconds(dep))}</DepartureTime>`,
          `  </VehicleJourney>`,
        ].join('\n'),
      );
    }

    const cal = calBySvc.get(trips[0].service_id);
    const startDate = toTxcDate(cal?.start_date) || toTxcDate(params.defaultServiceDateRange.startDate);
    const endDate = toTxcDate(cal?.end_date) || toTxcDate(params.defaultServiceDateRange.endDate);
    servicesXml.push(
      [
        `    <Service>`,
        `      <ServiceCode>${esc(serviceCode)}</ServiceCode>`,
        `      <Lines>`,
        `        <Line id="${lineId}">`,
        `          <LineName>${esc(lineName)}</LineName>`,
        `        </Line>`,
        `      </Lines>`,
        `      <OperatingPeriod>`,
        `        <StartDate>${esc(startDate)}</StartDate>`,
        endDate ? `        <EndDate>${esc(endDate)}</EndDate>` : '',
        `      </OperatingPeriod>`,
        operatingProfileXml(cal, '      '),
        `      <RegisteredOperatorRef>${opId}</RegisteredOperatorRef>`,
        `      <Mode>bus</Mode>`,
        `      <StandardService>`,
        `        <Origin>${esc(origin)}</Origin>`,
        `        <Destination>${esc(destination)}</Destination>`,
        journeyPatternsXml.join('\n'),
        `      </StandardService>`,
        `    </Service>`,
      ].filter(Boolean).join('\n'),
    );
  }

  // StopPoints — union of referenced stops, with ATCO where known.
  let stopsWithoutAtco = 0;
  const stopPointsXml: string[] = [];
  for (const stopId of Array.from(referencedStopIds)) {
    const s = stopById.get(stopId);
    if (!s) continue;
    const atco = atcoByGtfsId.get(stopId) || (looksLikeAtco(stopId) ? stopId : '');
    const ref = atco || `CUSTOM-${stopId}`;
    if (!atco) stopsWithoutAtco += 1;
    stopPointsXml.push(
      [
        `    <AnnotatedStopPointRef>`,
        `      <StopPointRef>${esc(ref)}</StopPointRef>`,
        `      <CommonName>${esc(s.stop_name || stopId)}</CommonName>`,
        s.stop_lon && s.stop_lat
          ? `      <Location><Longitude>${esc(s.stop_lon)}</Longitude><Latitude>${esc(s.stop_lat)}</Latitude></Location>`
          : '',
        `    </AnnotatedStopPointRef>`,
      ].filter(Boolean).join('\n'),
    );
  }
  if (stopsWithoutAtco > 0) {
    warnings.push(
      `${stopsWithoutAtco} stop(s) have no NaPTAN ATCO code and were exported with a synthetic StopPointRef — `
      + 'not registration-grade until matched to NaPTAN.',
    );
  }

  const operatorXml = [
    `    <Operator id="${opId}">`,
    `      <NationalOperatorCode>${esc(noc)}</NationalOperatorCode>`,
    `      <OperatorCode>${esc(noc)}</OperatorCode>`,
    `      <OperatorShortName>${esc(params.operator.operatorName)}</OperatorShortName>`,
    `      <OperatorNameOnLicence>${esc(params.operator.operatorName)}</OperatorNameOnLicence>`,
    params.operator.licenceNumber?.trim()
      ? `      <LicenceNumber>${esc(params.operator.licenceNumber.trim())}</LicenceNumber>`
      : '',
    `    </Operator>`,
  ].filter(Boolean).join('\n');

  const dt = esc(params.creationDateTime);
  const xml = [
    `<?xml version="1.0" encoding="UTF-8"?>`,
    `<TransXChange xmlns="http://www.transxchange.org.uk/" `
      + `xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance" `
      + `CreationDateTime="${dt}" ModificationDateTime="${dt}" Modification="new" `
      + `RevisionNumber="1" FileName="${esc(params.feedName || 'atlas')}.xml" `
      + `SchemaVersion="2.4" `
      + `xsi:schemaLocation="http://www.transxchange.org.uk/ `
      + `http://www.transxchange.org.uk/schema/2.4/TransXChange_general.xsd">`,
    // TransXChange 2.4 root sequence is strict: StopPoints, RouteSections,
    // Routes, JourneyPatternSections, Operators, Services, VehicleJourneys.
    `  <StopPoints>`,
    stopPointsXml.join('\n'),
    `  </StopPoints>`,
    `  <RouteSections>`,
    routeSectionsXml.join('\n'),
    `  </RouteSections>`,
    `  <Routes>`,
    routesXml.join('\n'),
    `  </Routes>`,
    `  <JourneyPatternSections>`,
    jpSectionsXml.join('\n'),
    `  </JourneyPatternSections>`,
    `  <Operators>`,
    operatorXml,
    `  </Operators>`,
    `  <Services>`,
    servicesXml.join('\n'),
    `  </Services>`,
    `  <VehicleJourneys>`,
    vehicleJourneysXml.join('\n'),
    `  </VehicleJourneys>`,
    `</TransXChange>`,
  ].join('\n');

  return {
    xml,
    warnings,
    skippedRoutes: params.skippedRoutes ?? [],
    counts: {
      services: servicesXml.length,
      lines: lineCounter,
      journeyPatterns: jpCounter,
      vehicleJourneys: vjCounter,
      stopPoints: stopPointsXml.length,
      stopsWithoutAtco,
    },
  };
}

// --- public: FixedRoutes → TransXChange ------------------------------------

export function buildBodsTransXChange(
  state: FixedRoutesState,
  options: BodsExportOptions,
): BodsExportResult {
  const gtfs = buildGtfsFeed(state, {
    selectedRouteIds: options.selectedRouteIds,
    defaultServiceDays: options.defaultServiceDays,
    defaultServiceDateRange: options.defaultServiceDateRange,
    defaultRouteType: options.defaultRouteType,
    meta: options.meta,
  });

  const atcoByGtfsId = new Map<string, string>();
  for (const s of state.stops) {
    if (s.atcoCode?.trim()) atcoByGtfsId.set(gtfsStopIdBase(s), s.atcoCode.trim());
  }

  return transXChangeFromGtfsFeed(gtfs.feed, {
    operator: options.operator,
    feedName: options.meta.feedName,
    creationDateTime: options.creationDateTime,
    defaultServiceDateRange: options.defaultServiceDateRange,
    atcoByGtfsId,
    seedWarnings: gtfs.warnings,
    skippedRoutes: gtfs.skippedRoutes,
  });
}

/** Wrap the TransXChange XML in a `.zip` (BODS accepts a zipped TransXChange dataset). */
export async function writeBodsZip(result: BodsExportResult, baseName: string): Promise<Blob> {
  const zip = new JSZip();
  zip.file(`${baseName}.xml`, result.xml);
  return zip.generateAsync({ type: 'blob', compression: 'DEFLATE' });
}
